import React, { useState } from 'react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { Collapse } from 'antd';

type PricingTier = {
  name: string;
  id: string;
  href: string;
  price: string;
  description: string;
  features: string[];
  attributionsLimit: string;
  attributionsPerView: string;
  mostPopular?: boolean;
};

type FAQ = {
  question: string;
  answer: string;
};

type ComparisonFeature = {
  name: string;
  tiers?: string[];
  values?: string[];
};

const faqs: FAQ[] = [
  {
    question: 'What are attributions?',
    answer:
      'Attributions are identity data points that help you understand who owns or interacts with blockchain addresses. This includes information about exchanges, protocols, known entities, and more.',
  },
  {
    question: 'How are attribution limits calculated?',
    answer:
      'Attribution limits are calculated based on the total number of unique identity data points you access per month. Each time you view an address or transaction that reveals new identity information, it counts towards your monthly limit.',
  },
  {
    question: 'What happens if I exceed my attribution limit?',
    answer:
      "When you reach your attribution limit, you'll still be able to use the basic block explorer features, but identity information will be hidden until your limit resets the following month or you upgrade your plan.",
  },
  {
    question: 'Can I upgrade or downgrade my plan at any time?',
    answer:
      "Yes, you can change your plan at any time. When upgrading, you'll get immediate access to the new limits. When downgrading, the change will take effect at the start of your next billing cycle.",
  },
];

const tiers: PricingTier[] = [
  {
    name: 'Developer',
    id: 'tier-developer',
    href: '#',
    price: '$99',
    description: 'Perfect for getting started with blockchain exploration.',
    features: [
      'Basic blockexplorer functionalities',
      'Community support',
      'Rate-limited views per day',
      'API access',
      'Basic data export',
    ],
    attributionsLimit: '10,000 attributions/month',
    attributionsPerView: '10 attributions/request',
  },
  {
    name: 'Business',
    id: 'tier-business',
    href: '#',
    price: '$*',
    description: 'Ideal for enthusiasts and small teams.',
    features: [
      'Full blockexplorer functionalities',
      'Email support',
      'Advanced data export',
      'API access',
    ],
    attributionsLimit: '100,000 attributions/month',
    attributionsPerView: '50 attributions/request',
    mostPopular: false,
  },
  {
    name: 'Professional',
    id: 'tier-professional',
    href: '#',
    price: '$*',
    description: 'For professional traders and medium-sized teams.',
    features: [
      'Priority email support',
      'Full API access',
      'Advanced analytics',
      'Custom data exports',
    ],
    attributionsLimit: '500,000 attributions/month',
    attributionsPerView: '100 attributions/request',
    mostPopular: true,
  },
  {
    name: 'Enterprise',
    id: 'tier-enterprise',
    href: '#',
    price: 'Custom',
    description:
      'Dedicated support and custom solutions for large organizations.',
    features: [
      'Dedicated account manager',
      'Custom data analytics',
      'SLA guaranteed uptime',
      'Onboarding & training',
    ],
    attributionsLimit: 'Unlimited attributions',
    attributionsPerView: 'Custom limits',
  },
];

const comparisonFeatures: ComparisonFeature[] = [
  {
    name: 'Attribution Limit',
    values: ['10,000/mo', '100,000/mo', '500,000/mo', 'Unlimited'],
  },
  { name: 'Attributions per Request', values: ['5', '50', '100', 'Custom'] },
  {
    name: 'Basic Explorer Features',
    tiers: ['Developer', 'Business', 'Professional', 'Enterprise'],
  },
  {
    name: 'API Access',
    tiers: ['Developer', 'Business', 'Professional', 'Enterprise'],
  },
  {
    name: 'Higher Rate Limits',
    tiers: ['Business', 'Professional', 'Enterprise'],
  },
  // { name: 'Data Export', tiers: ['Standard', 'Professional', 'Enterprise'] },
  // { name: 'Custom Analytics', tiers: ['Professional', 'Enterprise'] },
  { name: 'Priority Support', tiers: ['Professional', 'Enterprise'] },
  { name: 'Dedicated Account Manager', tiers: ['Enterprise'] },
];

export const Pricing: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>(
    'monthly',
  );

  return (
    <div className="bg-white">
      {/* Header section */}
      <div className="mx-auto max-w-7xl px-6 pb-12 pt-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-base font-semibold leading-7 text-indigo-600">
            Pricing
          </h1>
          <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Pricing Plans for Every Need
          </p>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            Choose the perfect plan to unlock powerful blockchain identity data
            and attributions.
          </p>
        </div>

        {/* Billing toggle */}
        {/* <div className="mt-16 flex justify-center">
          <div className="relative">
            <div className="flex rounded-lg bg-gray-100 p-1">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`${
                  billingPeriod === 'monthly'
                    ? 'bg-white shadow-sm'
                    : 'hover:bg-gray-50'
                } relative w-32 rounded-md py-2 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
              >
                Monthly billing
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`${
                  billingPeriod === 'annual'
                    ? 'bg-white shadow-sm'
                    : 'hover:bg-gray-50'
                } relative w-32 rounded-md py-2 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
              >
                Annual billing
              </button>
            </div>
          </div>
        </div> */}
      </div>

      {/* Pricing cards */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="isolate mx-auto grid max-w-md grid-cols-1 gap-y-8 lg:mx-0 lg:max-w-none lg:grid-cols-4">
          {tiers.map((tier, tierIdx) => (
            <div
              key={tier.id}
              className={`flex flex-col justify-between rounded-3xl bg-white p-8 ring-1 ring-gray-200 xl:p-10 ${tier.mostPopular ? 'lg:z-10 lg:rounded-b-none' : ''} ${tierIdx === 0 ? 'lg:rounded-r-none' : ''} ${tierIdx === tiers.length - 1 ? 'lg:rounded-l-none' : ''} ${tierIdx !== 0 && tierIdx !== tiers.length - 1 ? 'lg:rounded-none' : ''} `}
            >
              <div>
                <div className="flex items-center justify-between gap-x-4">
                  <h3
                    id={tier.id}
                    className={`text-lg font-semibold leading-8 ${
                      tier.mostPopular ? 'text-indigo-600' : 'text-gray-900'
                    }`}
                  >
                    {tier.name}
                  </h3>
                  {tier.mostPopular && (
                    <p className="rounded-full bg-indigo-600/10 px-2.5 py-1 text-xs font-semibold leading-5 text-indigo-600">
                      Most popular
                    </p>
                  )}
                </div>
                <p className="mt-4 text-sm leading-6 text-gray-600">
                  {tier.description}
                </p>
                <p className="mt-6 flex items-baseline gap-x-1">
                  <span className="text-4xl font-bold tracking-tight text-gray-900">
                    {billingPeriod === 'annual' && tier.price !== 'Custom'
                      ? `$${parseInt(tier.price.replace('$', '')) * 10}`
                      : tier.price}
                  </span>
                  {tier.price !== 'Custom' && (
                    <span className="text-sm font-semibold leading-6 text-gray-600">
                      /{billingPeriod === 'annual' ? 'year' : 'month'}
                    </span>
                  )}
                </p>
                <div className="mt-8 space-y-3">
                  <div className="flex gap-x-3">
                    <CheckIcon
                      className="h-6 w-5 flex-none text-indigo-600"
                      aria-hidden="true"
                    />
                    <span className="text-sm leading-6 text-gray-600">
                      {tier.attributionsLimit}
                    </span>
                  </div>
                  <div className="flex gap-x-3">
                    <CheckIcon
                      className="h-6 w-5 flex-none text-indigo-600"
                      aria-hidden="true"
                    />
                    <span className="text-sm leading-6 text-gray-600">
                      {tier.attributionsPerView}
                    </span>
                  </div>
                  {tier.features.map((feature) => (
                    <div key={feature} className="flex gap-x-3">
                      <CheckIcon
                        className="h-6 w-5 flex-none text-indigo-600"
                        aria-hidden="true"
                      />
                      <span className="text-sm leading-6 text-gray-600">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <a
                href={tier.href}
                aria-describedby={tier.id}
                className={`mt-8 block rounded-md px-3 py-2 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                  tier.mostPopular
                    ? 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-indigo-600'
                    : 'bg-white text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:ring-indigo-300'
                } `}
              >
                Get started
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison table */}
      <div className="mx-auto mt-24 max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-2xl font-bold leading-10 tracking-tight text-gray-900">
            Compare Plans
          </h2>
          <p className="mt-4 text-base leading-7 text-gray-600">
            Detailed comparison of features across all plans
          </p>
          <div className="mt-8 border-t border-gray-200">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="py-4 pr-4 text-left text-sm font-semibold text-gray-900">
                    Feature
                  </th>
                  {tiers.map((tier) => (
                    <th
                      key={tier.name}
                      className="px-4 py-4 text-left text-sm font-semibold text-gray-900"
                    >
                      {tier.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((feature, featureIdx) => (
                  <tr
                    key={feature.name}
                    className={featureIdx % 2 === 0 ? 'bg-gray-50' : undefined}
                  >
                    <td className="py-4 pr-4 text-sm font-medium text-gray-900">
                      {feature.name}
                    </td>
                    {tiers.map((tier) => (
                      <td
                        key={`${tier.name}-${feature.name}`}
                        className="px-4 py-4 text-sm text-gray-500"
                      >
                        {feature.values ? (
                          feature.values[tiers.indexOf(tier)]
                        ) : feature.tiers?.includes(tier.name) ? (
                          <CheckIcon
                            className="h-5 w-5 text-indigo-600"
                            aria-hidden="true"
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* FAQ section */}
      <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <h2 className="mb-10 text-2xl font-bold leading-10 tracking-tight text-gray-900">
            Frequently asked questions
          </h2>
          <Collapse
            accordion
            items={faqs.map((faq) => ({
              key: faq.question,
              label: (
                <span className="text-lg font-semibold">{faq.question}</span>
              ),
              children: (
                <div className="text-base text-gray-600">{faq.answer}</div>
              ),
            }))}
          />
        </div>
      </div>
    </div>
  );
};
